import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, leadingIcon, trailingIcon, ...props }, ref) => {
    if (leadingIcon || trailingIcon) {
      return (
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg border bg-white px-3 transition-colors',
            'border-ink-200 focus-within:border-brand-500 focus-within:ring-brand-500/20 focus-within:ring-4',
            invalid &&
              'border-danger-500 focus-within:border-danger-500 focus-within:ring-danger-500/20',
            'shadow-soft',
          )}
        >
          {leadingIcon && <span className="text-ink-500 shrink-0">{leadingIcon}</span>}
          <input
            ref={ref}
            className={cn(
              'text-ink-900 placeholder:text-ink-400 min-w-0 flex-1 bg-transparent py-2.5 text-sm outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              className,
            )}
            aria-invalid={invalid || undefined}
            {...props}
          />
          {trailingIcon && <span className="text-ink-500 shrink-0">{trailingIcon}</span>}
        </div>
      );
    }
    return (
      <input
        ref={ref}
        className={cn(
          'text-ink-900 shadow-soft w-full rounded-lg border bg-white px-3 py-2.5 text-sm outline-none transition-colors',
          'border-ink-200 placeholder:text-ink-400',
          'focus:border-brand-500 focus:ring-brand-500/20 focus:ring-4',
          'disabled:cursor-not-allowed disabled:opacity-50',
          invalid && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20',
          className,
        )}
        aria-invalid={invalid || undefined}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';
